/* eslint-disable react/no-unescaped-entities */
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Header, Footer } from "../components/Layout";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  ArrowLeft, Coins, Lock, Lightning, Confetti, ShieldCheck, Warning,
  CheckCircle, Star,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import PlaceAutocompleteInput from "../components/PlaceAutocompleteInput";

// =============================================================================
// VAULT THEME — money-green + gold against deep ink. Distinct from every
// other tool page so it's instantly recognisable as the high-stakes admin
// tool.
// =============================================================================
const TH = {
  bg:        "#06120E",
  surface:   "#0E1F18",
  surfaceHi: "#15291F",
  border:    "#1F3D2E",
  borderHi:  "#3A6951",
  gold:      "#F0C75E",
  goldDim:   "#7E6326",
  green:     "#5EE0A8",
  greenDim:  "#2C7A55",
  rose:      "#FF7A8E",
  amber:     "#FFB155",
  text:      "#F0F8F3",
  textMute:  "#8FAA9C",
  textDim:   "#5C7568",
};

const TIER_STYLE = {
  SURESHOT: { color: TH.gold,  bg: `${TH.gold}1A`,  border: `${TH.gold}88`,  label: "SURESHOT", glow: 26 },
  STRONG:   { color: TH.green, bg: `${TH.green}1A`, border: `${TH.green}88`, label: "STRONG",   glow: 18 },
  GOOD:     { color: TH.amber, bg: `${TH.amber}1A`, border: `${TH.amber}66`, label: "GOOD",     glow: 12 },
};

export default function LotteryIndicatorPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [form, setForm] = useState({ date: "", time: "", place: "", timezone: "Asia/Kolkata" });
  // Default range = next 12 years from today. The operator can override
  // to any past or future custom range — engine walks the full birth-
  // anchored Vimshottari tree when any part of the range is in the past.
  const today = new Date().toISOString().slice(0, 10);
  const twelveYearsAhead = new Date(Date.now() + 12 * 365.25 * 86400000).toISOString().slice(0, 10);
  const [rangeFrom, setRangeFrom] = useState(today);
  const [rangeTo, setRangeTo] = useState(twelveYearsAhead);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [expandedIdx, setExpandedIdx] = useState(null);
  const [celebs, setCelebs] = useState([]);
  const [loadedSubject, setLoadedSubject] = useState(null);   // {name, source} chip
  const [loadingPrefill, setLoadingPrefill] = useState(false);
  const [celebQuery, setCelebQuery] = useState("");
  const [celebSearchOpen, setCelebSearchOpen] = useState(false);

  // Super-admin gate — if not authorised, redirect home with a polite toast.
  useEffect(() => {
    if (authLoading) return;
    if (!user || !user.is_super_admin) {
      toast.error("Lottery Indicator is a Super-Admin tool.");
      navigate("/", { replace: true });
    }
  }, [user, authLoading, navigate]);

  // Lazy-load celebrity list once the super-admin lands on the page
  useEffect(() => {
    if (!user?.is_super_admin) return;
    api.get("/admin/tools/lottery-indicator/celebrities")
      .then(({ data }) => setCelebs(data.celebrities || []))
      .catch(() => { /* silent — picker just won't appear */ });
  }, [user]);

  const loadMyBirthDetails = async () => {
    setLoadingPrefill(true);
    try {
      const { data } = await api.get("/customer/kundali");
      const bd = data.birth_details || {};
      setForm({
        date: bd.date || "",
        time: bd.time || "",
        place: bd.place || "",
        timezone: bd.timezone || "Asia/Kolkata",
      });
      setLoadedSubject({ name: user?.name || "Me", source: "your saved profile" });
      toast.success("Your birth details loaded.");
    } catch (err) {
      toast.error(err?.response?.status === 404
        ? "Save your birth details once from the Free Kundali tool first."
        : "Couldn't load your details.");
    } finally {
      setLoadingPrefill(false);
    }
  };

  const loadCelebrity = (slug) => {
    if (!slug) return;
    const c = celebs.find((x) => x.slug === slug);
    if (!c) return;
    setForm({
      date: c.dob,
      time: c.time,
      place: c.place,
      timezone: c.tz || "Asia/Kolkata",
    });
    setLoadedSubject({
      name: c.name,
      source: `${c.category.replace("-", " ")} · ${c.rodden_rating}-rated · ${c.country}`,
      story: c.story,
      rise: c.rise_event_label ? `${c.rise_event_label} (${(c.rise_event_date || "").slice(0, 4)})` : "",
    });
    toast.success(`Loaded ${c.name}'s birth chart.`);
  };

  const analyze = async (e) => {
    e?.preventDefault();
    if (!form.date || !form.time || !form.place) {
      toast.error("Fill date, time and place of birth.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/tools/lottery-indicator", {
        birth_details: form,
        horizon_years: 12,
        range_from: rangeFrom,
        range_to: rangeTo,
        max_results: 12,
      });
      setResult(data);
      setExpandedIdx(0);
      setTimeout(() => {
        document.querySelector("[data-testid='lottery-result']")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not compute analysis.");
    } finally {
      setBusy(false);
    }
  };

  if (authLoading || !user?.is_super_admin) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: TH.bg, color: TH.text }}>
        <div className="flex items-center gap-2 font-mono text-xs">
          <Lock size={14} /> Verifying super-admin access...
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] flex flex-col" style={{ backgroundColor: TH.bg, color: TH.text }} data-testid="lottery-page">
      <div
        className="fixed inset-0 pointer-events-none -z-10"
        style={{
          background: `radial-gradient(ellipse at 0% 0%, ${TH.green}10 0%, transparent 50%),
                       radial-gradient(ellipse at 100% 100%, ${TH.gold}10 0%, transparent 50%),
                       ${TH.bg}`,
        }}
      />
      <Header />
      <main className="flex-1 max-w-5xl mx-auto w-full px-4 sm:px-6 lg:px-10 py-6 sm:py-10">
        <button onClick={() => navigate(-1)} className="md:hidden flex items-center gap-1 text-sm mb-4" style={{ color: TH.textMute }}>
          <ArrowLeft size={16} /> Back
        </button>

        {/* Admin badge */}
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-4 font-mono text-[10px] uppercase tracking-[0.2em]"
          style={{ backgroundColor: `${TH.gold}1A`, color: TH.gold, border: `1px solid ${TH.gold}55` }}>
          <Lock size={11} weight="fill" /> Super-Admin · Internal Tool
        </div>

        <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-light leading-tight" style={{ color: TH.text }}>
          Lottery <span className="italic" style={{ color: TH.gold }}>Indicator</span>
        </h1>
        <p className="text-sm sm:text-base mt-4 max-w-3xl font-light leading-relaxed" style={{ color: TH.textMute }}>
          Triangulates KP's lottery house-combo (<b style={{ color: TH.green }}>2 · 3 · 5 · 6 · 8 · 11</b>) against
          Vimshottari activation, <b style={{ color: TH.gold }}>Kalachakra-dasha</b> rashi-flow and <b style={{ color: TH.gold }}>Varshaphal</b> year-lord
          to pinpoint MD-AD-PD windows where all three independently green-light speculation. Cross-validated against a curated
          fingerprint dataset of <b>real lottery winners</b> for natal propensity score.
        </p>

        {/* FORM */}
        <form
          onSubmit={analyze}
          className="mt-8 rounded-2xl p-5 sm:p-7"
          style={{ backgroundColor: TH.surface, border: `1px solid ${TH.border}`, boxShadow: `0 12px 60px -20px ${TH.green}33` }}
        >
          {/* Auto-fetch helpers — speed up research */}
          <div className="mb-5 rounded-xl p-3 sm:p-4"
            style={{ backgroundColor: TH.surfaceHi, border: `1px dashed ${TH.border}` }}>
            <div className="flex items-center gap-2 font-mono text-[9px] uppercase tracking-[0.2em] mb-2.5"
              style={{ color: TH.gold }}>
              <Lightning size={12} weight="duotone" />
              Auto-fetch · Skip retyping
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <button type="button" onClick={loadMyBirthDetails} disabled={loadingPrefill}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md font-mono text-xs transition-all disabled:opacity-50"
                style={{ backgroundColor: TH.bg, color: TH.green, border: `1px solid ${TH.green}66` }}
                data-testid="lot-load-me">
                <CheckCircle size={12} weight="fill" />
                {loadingPrefill ? "Loading…" : "Use my birth details"}
              </button>

              {celebs.length > 0 && (
                <div className="relative flex items-center gap-2 flex-1 min-w-[260px]">
                  <span className="font-mono text-[9px] uppercase tracking-[0.18em]" style={{ color: TH.textDim }}>or</span>
                  <div className="relative flex-1">
                    <input
                      type="text"
                      value={celebQuery}
                      onFocus={() => setCelebSearchOpen(true)}
                      onChange={(e) => { setCelebQuery(e.target.value); setCelebSearchOpen(true); }}
                      onBlur={() => setTimeout(() => setCelebSearchOpen(false), 180)}
                      placeholder="🔎  Search celebrity — Jobs, Ambani, Castro, Chaplin…"
                      data-testid="lot-celeb-search"
                      className="w-full px-3 py-2 rounded-md font-mono text-xs"
                      style={{ backgroundColor: TH.bg, color: TH.gold, border: `1px solid ${TH.border}`, colorScheme: "dark" }}
                    />
                    {celebSearchOpen && (
                      <div
                        className="absolute z-50 left-0 right-0 top-full mt-1 max-h-[340px] overflow-y-auto rounded-md shadow-2xl"
                        style={{ backgroundColor: TH.bg, border: `1px solid ${TH.border}`, boxShadow: `0 10px 40px -10px ${TH.green}66` }}
                        data-testid="lot-celeb-results"
                      >
                        {(() => {
                          const q = celebQuery.trim().toLowerCase();
                          const filtered = q
                            ? celebs.filter((c) =>
                                c.name.toLowerCase().includes(q)
                                || (c.industry || "").toLowerCase().includes(q)
                                || (c.country || "").toLowerCase().includes(q)
                                || c.category.toLowerCase().includes(q)
                                || (c.story || "").toLowerCase().includes(q)
                              ).slice(0, 30)
                            : celebs.slice(0, 12);
                          if (!filtered.length) {
                            return (
                              <div className="px-3 py-4 font-mono text-xs text-center" style={{ color: TH.textDim }}>
                                No celebrity matches "{celebQuery}".
                              </div>
                            );
                          }
                          // group by category preserving order
                          const grouped = filtered.reduce((acc, c) => {
                            (acc[c.category] = acc[c.category] || []).push(c);
                            return acc;
                          }, {});
                          return Object.entries(grouped).map(([cat, list]) => (
                            <div key={cat}>
                              <div className="px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.2em] sticky top-0"
                                style={{ color: TH.gold, backgroundColor: TH.surfaceHi, borderBottom: `1px solid ${TH.border}` }}>
                                {cat.replace("-", " ")} · {list.length}
                              </div>
                              {list.map((c) => (
                                <button
                                  type="button"
                                  key={c.slug}
                                  onMouseDown={(e) => { e.preventDefault(); loadCelebrity(c.slug); setCelebQuery(c.name); setCelebSearchOpen(false); }}
                                  className="w-full text-left px-3 py-2 transition-colors hover:opacity-100 flex items-start gap-3"
                                  style={{ backgroundColor: TH.bg, borderBottom: `1px solid ${TH.border}66`, color: TH.text }}
                                  data-testid={`lot-celeb-opt-${c.slug}`}
                                >
                                  <div className="flex-1 min-w-0">
                                    <div className="font-mono text-xs truncate" style={{ color: TH.gold }}>
                                      {c.name}
                                      <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded font-bold"
                                        style={{ backgroundColor: `${TH.green}22`, color: TH.green }}>
                                        {c.rodden_rating}
                                      </span>
                                    </div>
                                    <div className="font-mono text-[10px] mt-0.5" style={{ color: TH.textMute }}>
                                      {c.dob} {c.time} · {c.place || c.country}
                                    </div>
                                    {c.rise_event_label && (
                                      <div className="text-[10px] mt-0.5 italic" style={{ color: TH.textDim }}>
                                        ↳ {c.rise_event_label} ({(c.rise_event_date || "").slice(0, 4)})
                                      </div>
                                    )}
                                  </div>
                                </button>
                              ))}
                            </div>
                          ));
                        })()}
                      </div>
                    )}
                  </div>
                  {celebQuery && (
                    <button type="button" onClick={() => { setCelebQuery(""); setCelebSearchOpen(false); }}
                      className="font-mono text-[10px] uppercase tracking-wider px-2 py-1 rounded"
                      style={{ color: TH.textDim, border: `1px solid ${TH.border}` }}
                      data-testid="lot-celeb-clear">
                      clear
                    </button>
                  )}
                </div>
              )}
            </div>

            {loadedSubject && (
              <div className="mt-3 px-3 py-2 rounded-md font-mono text-[11px]"
                style={{ backgroundColor: `${TH.gold}11`, border: `1px solid ${TH.gold}44`, color: TH.text }}
                data-testid="lot-loaded-chip">
                <div className="flex items-start gap-2">
                  <Star size={12} weight="fill" style={{ color: TH.gold, marginTop: 2 }} />
                  <div className="flex-1">
                    <div><b style={{ color: TH.gold }}>{loadedSubject.name}</b> · loaded from {loadedSubject.source}</div>
                    {loadedSubject.rise && (
                      <div className="mt-0.5" style={{ color: TH.textMute }}>Rise event: {loadedSubject.rise}</div>
                    )}
                    {loadedSubject.story && (
                      <div className="mt-0.5" style={{ color: TH.textDim }}>{loadedSubject.story}</div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <label className="block">
              <span className="font-mono text-[9px] uppercase tracking-[0.2em]" style={{ color: TH.textDim }}>Date of birth</span>
              <input type="date" required value={form.date}
                onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
                className="mt-1.5 w-full px-3 py-2.5 rounded-md font-mono text-sm"
                style={{ backgroundColor: TH.surfaceHi, color: TH.text, border: `1px solid ${TH.border}`, colorScheme: "dark" }}
                data-testid="lot-date" />
            </label>
            <label className="block">
              <span className="font-mono text-[9px] uppercase tracking-[0.2em]" style={{ color: TH.textDim }}>Time of birth</span>
              <input type="time" required value={form.time}
                onChange={(e) => setForm((f) => ({ ...f, time: e.target.value }))}
                className="mt-1.5 w-full px-3 py-2.5 rounded-md font-mono text-sm"
                style={{ backgroundColor: TH.surfaceHi, color: TH.text, border: `1px solid ${TH.border}`, colorScheme: "dark" }}
                data-testid="lot-time" />
            </label>
            <label className="block">
              <span className="font-mono text-[9px] uppercase tracking-[0.2em]" style={{ color: TH.textDim }}>Place of birth</span>
              <div className="mt-1.5">
                <PlaceAutocompleteInput
                  value={form.place}
                  onChange={(v) => setForm((f) => ({ ...f, place: v }))}
                  onPick={({ name, timezone }) => setForm((f) => ({ ...f, place: name, timezone: timezone || f.timezone }))}
                  placeholder="Mumbai, India"
                  testId="lot-place"
                  iconColor={TH.textDim}
                  inputClass="w-full px-3 py-2.5 pr-9 rounded-md font-mono text-sm outline-none"
                  dropdownClass="absolute top-full left-0 right-0 mt-1 max-h-60 overflow-y-auto rounded-md z-30 shadow-2xl"
                />
                <style>{`
                  [data-testid="lot-place"] { background-color: ${TH.surfaceHi}; color: ${TH.text}; border: 1px solid ${TH.border}; }
                  [data-testid="lot-place"]:focus { border-color: ${TH.gold}; }
                  [data-testid="lot-place-suggestions"] { background-color: ${TH.surfaceHi}; border: 1px solid ${TH.border}; color: ${TH.text}; }
                `}</style>
              </div>
            </label>
          </div>

          <div className="mt-5">
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] mb-2" style={{ color: TH.textDim }}>
              Scan range — pick any past or future window
            </div>
            <div className="flex flex-wrap items-end gap-3">
              <label className="flex flex-col gap-1.5">
                <span className="font-mono text-[9px] uppercase tracking-wider" style={{ color: TH.textDim }}>From</span>
                <input type="date" required value={rangeFrom}
                  onChange={(e) => setRangeFrom(e.target.value)}
                  className="px-3 py-2 rounded-md font-mono text-xs"
                  style={{ backgroundColor: TH.surfaceHi, color: TH.gold, border: `1px solid ${TH.border}`, colorScheme: "dark" }}
                  data-testid="lot-range-from" />
              </label>
              <span className="pb-2" style={{ color: TH.textDim }}>→</span>
              <label className="flex flex-col gap-1.5">
                <span className="font-mono text-[9px] uppercase tracking-wider" style={{ color: TH.textDim }}>To</span>
                <input type="date" required value={rangeTo}
                  onChange={(e) => setRangeTo(e.target.value)}
                  className="px-3 py-2 rounded-md font-mono text-xs"
                  style={{ backgroundColor: TH.surfaceHi, color: TH.gold, border: `1px solid ${TH.border}`, colorScheme: "dark" }}
                  data-testid="lot-range-to" />
              </label>

              {/* Quick presets */}
              <div className="flex flex-wrap items-end gap-1.5 pb-0.5">
                {[
                  { label: "Next 3 yrs",  from: today, to: new Date(Date.now() + 3 * 365.25 * 86400000).toISOString().slice(0,10) },
                  { label: "Next 12 yrs", from: today, to: twelveYearsAhead },
                  { label: "This year",   from: `${new Date().getFullYear()}-01-01`, to: `${new Date().getFullYear()}-12-31` },
                  { label: "Past 10 yrs", from: new Date(Date.now() - 10 * 365.25 * 86400000).toISOString().slice(0,10), to: today },
                  { label: "Full life",   from: form.date || "1900-01-01", to: twelveYearsAhead },
                ].map((p) => (
                  <button key={p.label} type="button"
                    onClick={() => { setRangeFrom(p.from); setRangeTo(p.to); }}
                    className="px-2.5 py-1.5 rounded-md font-mono text-[10px] uppercase tracking-wider transition-all hover:opacity-100"
                    style={{ backgroundColor: TH.bg, color: TH.greenDim, border: `1px solid ${TH.border}`, opacity: 0.75 }}
                    data-testid={`lot-preset-${p.label.toLowerCase().replace(/\s+/g, "-")}`}>
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-5 flex flex-wrap items-center justify-end gap-3">
            <button
              type="submit" disabled={busy}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-mono text-xs font-bold tracking-wider transition-all disabled:opacity-50"
              style={{ background: `linear-gradient(135deg, ${TH.green}, ${TH.gold})`, color: TH.bg, boxShadow: `0 0 28px ${TH.green}55` }}
              data-testid="lot-submit"
            >
              <Lightning size={14} weight="fill" />
              {busy ? "COMPUTING…" : "FIND WINDOWS"}
            </button>
          </div>
        </form>

        {result && (
          <LotteryResults result={result} expandedIdx={expandedIdx} setExpandedIdx={setExpandedIdx} />
        )}
      </main>
      <Footer />
    </div>
  );
}

// =============================================================================
// RESULTS
// =============================================================================
const LotteryResults = ({ result, expandedIdx, setExpandedIdx }) => {
  const { natal, natal_wealth, windows, summary, top_picks_3yr, nakshatra_lottery } = result;
  return (
    <div className="mt-10 space-y-8" data-testid="lottery-result">
      {/* Summary banner */}
      <div className="rounded-2xl p-5 sm:p-7" style={{
        background: `linear-gradient(135deg, ${TH.surface}, ${TH.surfaceHi})`,
        border: `1px solid ${TH.gold}55`,
        boxShadow: `0 0 40px ${TH.gold}22`,
      }}>
        <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: TH.gold }}>
          <Coins size={14} weight="duotone" /> Composite verdict
        </div>
        <div className="mt-2 text-sm sm:text-base leading-relaxed font-light"
          style={{ color: TH.text }}
          dangerouslySetInnerHTML={{ __html: formatBold(summary) }} />

        {/* Dual natal propensity gauges */}
        <div className="mt-4 pt-4 grid sm:grid-cols-2 gap-5" style={{ borderTop: `1px solid ${TH.border}` }}>
          <NatalGauge natal={natal} label="Lottery Propensity"
            subtitle="vs curated pure-jackpot winners (Mavis Wanczyk, Khichi, Hatkar…)"
            color={TH.gold} matchesLabel="winner-pattern overlap" testId="lot-natal-matches" />
          {natal_wealth && (
            <NatalGauge natal={natal_wealth} label="Wealth-Builder Propensity"
              subtitle="vs 45 billionaires + rags-to-riches (Ambani, Jobs, Carnegie…)"
              color={TH.green} matchesLabel="builder-pattern overlap" testId="lot-natal-wealth-matches" />
          )}
        </div>
      </div>

      {/* Top 2 picks · next 3 yrs */}
      {Array.isArray(top_picks_3yr) && top_picks_3yr.length > 0 && (
        <TopPicksSection picks={top_picks_3yr} />
      )}

      {/* Nakshatra Lottery — Venus×Mars MD→AD pair scan */}
      {nakshatra_lottery && (
        <NakshatraLotterySection nl={nakshatra_lottery} />
      )}

      {/* Window cards */}
      <section>
        <div className="mb-3">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em]" style={{ color: TH.gold }}>// Top Windfall Windows</div>
          <div className="mt-1 text-sm sm:text-base font-light" style={{ color: TH.textMute }}>
            Ranked by composite score (KP combo × Vimshottari × Kalachakra × Varshaphal × natal propensity).
          </div>
        </div>
        {(windows || []).length === 0 ? (
          <div className="rounded-xl p-5 font-mono text-xs" style={{ backgroundColor: TH.surface, border: `1px solid ${TH.border}`, color: TH.textMute }}>
            <Warning size={16} className="inline mr-2" />
            No qualifying windows in this horizon. The chart's lottery combo isn't activating any clean MD-AD-PD triplet.
          </div>
        ) : (
          <div className="space-y-3">
            {windows.map((w, i) => (
              <WindowCard key={i} w={w} expanded={expandedIdx === i} onToggle={() => setExpandedIdx(expandedIdx === i ? null : i)} />
            ))}
          </div>
        )}
      </section>

      {/* Disclaimer */}
      <div className="rounded-xl p-4 font-mono text-[10px] leading-relaxed" style={{ backgroundColor: TH.surface, border: `1px solid ${TH.border}`, color: TH.textDim }}>
        <Warning size={12} className="inline mr-1" />
        <b style={{ color: TH.textMute }}>Internal-use only.</b> KP probability framework; not legal/financial advice.
        Even SURESHOT windows are probabilistic — never stake more than you can afford to lose. Past natal patterns of winners do not guarantee outcomes.
      </div>
    </div>
  );
};

const NAK_TIER_TINT = {
  SURESHOT: { bg: "linear-gradient(135deg,#3b1110,#5a1a14)", border: "#fb7185", chip: "#fb7185" },
  STRONG:   { bg: "linear-gradient(135deg,#1e1a08,#2e2410)", border: "#facc15", chip: "#facc15" },
  GOOD:     { bg: "linear-gradient(135deg,#0d1a14,#10261a)", border: "#34d399", chip: "#34d399" },
};

const NakshatraLotterySection = ({ nl }) => {
  const venus = nl.venus_planets || [];
  const mars = nl.mars_planets || [];
  const flavours = nl.flavours || [];
  const wins = nl.windows || [];
  return (
    <section className="rounded-2xl p-5 sm:p-7" style={{
      background: `linear-gradient(135deg, ${TH.surface}, ${TH.surfaceHi})`,
      border: `1px solid ${TH.gold}55`,
    }} data-testid="lottery-nakshatra-section">
      <div className="font-mono text-[10px] uppercase tracking-[0.25em] flex items-center gap-2 flex-wrap" style={{ color: TH.gold }}>
        <Coins size={14} weight="duotone" /> // Nakshatra Lottery · Venus × Mars dasha pairs
        {nl.scan_from && nl.scan_to && (
          <span className="ml-auto px-2 py-0.5 rounded font-mono text-[10px]"
            style={{ background: TH.gold + "1F", color: TH.gold, letterSpacing: "0.1em" }}
            data-testid="nakshatra-scan-range">
            scan range · {nl.scan_from} → {nl.scan_to}
          </span>
        )}
      </div>
      <p className="mt-2 text-xs sm:text-sm font-light leading-relaxed" style={{ color: TH.textMute }}>
        {nl.rule}
      </p>

      {/* Summary banner */}
      <div className="mt-4 text-sm sm:text-base leading-relaxed font-light"
        style={{ color: TH.text }}
        dangerouslySetInnerHTML={{ __html: formatBold(nl.summary || "") }}
        data-testid="nakshatra-summary"
      />

      {/* Natal classification table */}
      {flavours.length > 0 && (
        <div className="mt-5">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] mb-2" style={{ color: TH.textDim }}>
            Natal classification
          </div>
          <div className="rounded-xl overflow-hidden" style={{ border: `1px solid ${TH.border}` }}>
            <div className="overflow-x-auto">
              <table className="w-full text-xs sm:text-sm" data-testid="nakshatra-flavour-table">
                <thead style={{ backgroundColor: TH.surfaceHi }}>
                  <tr style={{ color: TH.textMute }}>
                    <th className="text-left py-2 px-3">Planet</th>
                    <th className="text-left py-2 px-3">Rashi</th>
                    <th className="text-left py-2 px-3">Nakshatra</th>
                    <th className="text-left py-2 px-3">Flavour</th>
                  </tr>
                </thead>
                <tbody>
                  {flavours.map((f) => {
                    const flavor = f.venus && f.mars ? "Venus + Mars"
                                 : f.venus ? "Venus"
                                 : f.mars ? "Mars" : "—";
                    const isHot = f.venus || f.mars;
                    return (
                      <tr key={f.planet} style={{ borderTop: `1px solid ${TH.border}`, color: TH.text }} data-testid={`nakshatra-row-${f.planet}`}>
                        <td className="py-2 px-3 font-medium">{f.planet}</td>
                        <td className="py-2 px-3" style={{ color: TH.textMute }}>{f.rashi}</td>
                        <td className="py-2 px-3" style={{ color: TH.textMute }}>
                          {f.nakshatra}
                          {f.is_sureshot_nakshatra && <span className="ml-1.5 text-[9px] font-bold" style={{ color: TH.gold }}>★ MARQUEE</span>}
                        </td>
                        <td className="py-2 px-3">
                          {isHot ? (
                            <span className="text-[10px] font-bold tracking-wider px-1.5 py-0.5 rounded"
                              style={{
                                background: f.venus && f.mars ? "#fb718533"
                                          : f.venus ? "#f472b633"
                                          : "#fb923c33",
                                color: f.venus && f.mars ? "#fb7185"
                                     : f.venus ? "#f472b6" : "#fb923c",
                              }}>
                              {flavor}
                            </span>
                          ) : (
                            <span style={{ color: TH.textDim }}>—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          <div className="mt-2 text-[10px] font-light" style={{ color: TH.textDim }}>
            Venus link: Purvaphalguni / Purvashadha / Bharani · Taurus / Libra &nbsp;·&nbsp;
            Mars link: Dhanistha / Mrigashira / Chitra · Aries / Scorpio &nbsp;·&nbsp;
            ★ Marquee = Purvashadha / Dhanistha / Mrigashira (sureshot signature in AD).
          </div>
        </div>
      )}

      {/* Pair windows */}
      <div className="mt-6">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] mb-3" style={{ color: TH.textDim }}>
          Venus × Mars pair windows · Pratyantar-level scan (MD → AD → PD)
        </div>
        {wins.length === 0 ? (
          <div className="rounded-xl p-4 font-mono text-xs" style={{ backgroundColor: TH.surface, border: `1px solid ${TH.border}`, color: TH.textMute }}>
            <Warning size={14} className="inline mr-2" />
            {!venus.length || !mars.length
              ? "Your chart cannot fire this technique — one of the two flavours is absent natally."
              : "Venus and Mars flavours both present natally, but no MD→AD pair surfaces across your entire 120-year chain."}
          </div>
        ) : (
          <div className="space-y-3">
            {wins.map((w, i) => {
              const tint = NAK_TIER_TINT[w.tier] || NAK_TIER_TINT.GOOD;
              const periodChip = {
                current:  { label: "LIVE NOW",   bg: TH.green + "33", fg: TH.green },
                upcoming: { label: "UPCOMING",   bg: TH.gold + "33",  fg: TH.gold },
                past:     { label: "PAST",       bg: "#94a3b833",     fg: "#94a3b8" },
              }[w.period] || null;
              const fade = w.period === "past" ? "opacity-60" : "";
              return (
                <div key={i} className={`rounded-xl p-4 ${fade}`} style={{
                  background: tint.bg,
                  border: `1px solid ${tint.border}55`,
                }} data-testid={`nakshatra-window-${i}`}>
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] font-bold tracking-wider px-2 py-0.5 rounded"
                          style={{ background: tint.chip + "33", color: tint.chip }}>
                          {w.tier}
                        </span>
                        {periodChip && (
                          <span className="text-[10px] font-bold tracking-wider px-2 py-0.5 rounded"
                            style={{ background: periodChip.bg, color: periodChip.fg }}>
                            {periodChip.label}
                          </span>
                        )}
                        {w.ad_in_sureshot_nakshatra && (
                          <span className="text-[10px] font-bold tracking-wider px-2 py-0.5 rounded"
                            style={{ background: TH.gold + "33", color: TH.gold }}>
                            ★ MARQUEE NAK
                          </span>
                        )}
                      </div>
                      <div className="mt-2 font-serif-display text-xl font-light" style={{ color: TH.text }}>
                        {w.md} MD → {w.ad} AD{w.pd && <> → {w.pd} PD</>}
                      </div>
                      <div className="mt-1 text-xs font-light" style={{ color: TH.textMute }}>
                        <b style={{ color: TH.text }}>{w.from}</b> &nbsp;→&nbsp; <b style={{ color: TH.text }}>{w.to}</b>
                      </div>
                    </div>
                    <div className="text-right text-xs font-light leading-snug" style={{ color: TH.textMute }}>
                      <div>{w.md}: {w.md_rashi} · {w.md_nakshatra}</div>
                      <div className="mt-0.5">{w.ad}: {w.ad_rashi} · {w.ad_nakshatra}</div>
                      {w.pd && <div className="mt-0.5">{w.pd}: {w.pd_rashi} · {w.pd_nakshatra}</div>}
                    </div>
                  </div>
                  {Array.isArray(w.reasons) && w.reasons.length > 0 && (
                    <ul className="mt-3 space-y-1.5 text-xs font-light" style={{ color: TH.text }}>
                      {w.reasons.map((r, j) => (
                        <li key={j} className="flex gap-2">
                          <span style={{ color: tint.chip }}>•</span>
                          <span>{r}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};


const NatalGauge = ({ natal, label = "Natal Lottery Propensity",
                      subtitle = "", color, matchesLabel = "pattern overlap",
                      testId = "lot-natal-matches" }) => {
  const pct = natal.score_pct;
  const matches = natal.matches || [];
  const accent = color || TH.gold;
  return (
    <div>
      <div className="flex items-baseline justify-between">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: TH.textDim }}>
            {label}
          </div>
          {subtitle && (
            <div className="text-[10px] font-light mt-0.5" style={{ color: TH.textMute }}>{subtitle}</div>
          )}
        </div>
        <div className="font-mono text-2xl font-bold" style={{ color: accent }}>{pct}%</div>
      </div>
      <div className="mt-2 h-2 rounded-full overflow-hidden" style={{ backgroundColor: TH.surfaceHi }}>
        <div className="h-full rounded-full"
          style={{
            width: `${Math.max(2, pct)}%`,
            background: `linear-gradient(90deg, ${TH.green}, ${accent})`,
            boxShadow: `0 0 14px ${accent}77`,
          }} />
      </div>
      <div className="mt-2 text-xs font-light" style={{ color: TH.text }}>{natal.tier}</div>
      {matches.length > 0 && (
        <details className="mt-3" data-testid={testId}>
          <summary className="cursor-pointer font-mono text-[10px] uppercase tracking-widest" style={{ color: TH.greenDim }}>
            ▶ {matches.length} {matchesLabel}{matches.length === 1 ? "" : "s"}
          </summary>
          <div className="mt-2 space-y-1.5">
            {matches.map((m) => (
              <div key={m.pattern} className="rounded-md px-3 py-2 text-[11px]" style={{ backgroundColor: TH.surfaceHi, border: `1px solid ${TH.border}` }}>
                <div className="flex items-center gap-1.5">
                  <Star size={10} weight="fill" color={TH.gold} />
                  <span className="font-mono font-bold" style={{ color: TH.gold }}>{m.pattern}</span>
                  <span className="font-mono text-[10px]" style={{ color: TH.textDim }}>· {m.matched}/{m.total} checks</span>
                </div>
                <div className="mt-1 leading-snug" style={{ color: TH.textMute }}>{m.criteria}</div>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
};

const WindowCard = ({ w, expanded, onToggle }) => {
  const s = TIER_STYLE[w.tier] || TIER_STYLE.GOOD;
  const greens = [
    { ok: w.kp_verdict === "green", label: "KP",         note: `${(w.kp_houses_hit || []).length}/6 houses · ${(w.kp_houses_hit || []).map((h) => "H" + h).join("·") || "—"}` },
    { ok: w.kalachakra?.green,      label: "Kalachakra", note: w.kalachakra?.note || "—" },
    { ok: w.varshaphal?.green,      label: "Varshaphal", note: w.varshaphal?.note || "—" },
  ];
  return (
    <button type="button" onClick={onToggle}
      className="w-full text-left rounded-xl overflow-hidden transition-all"
      style={{ backgroundColor: TH.surface, border: `1px solid ${s.border}`, boxShadow: expanded ? `0 0 ${s.glow}px ${s.color}44` : "none" }}
      data-testid={`lot-window-${w.from_iso}`}
    >
      <div className="p-4 sm:p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex-1">
            <div className="flex items-center gap-2 font-mono text-[9px] uppercase tracking-[0.2em]">
              <span className="px-2 py-0.5 rounded-full font-bold" style={{ backgroundColor: s.bg, color: s.color, border: `1px solid ${s.border}` }}>
                {s.label}
              </span>
              {w.is_current && (
                <span className="px-2 py-0.5 rounded-full font-bold" style={{ backgroundColor: `${TH.green}22`, color: TH.green, border: `1px solid ${TH.green}88` }}>
                  RUNNING NOW
                </span>
              )}
              {w.is_past && (
                <span className="px-2 py-0.5 rounded-full font-bold" style={{ backgroundColor: `${TH.textDim}22`, color: TH.textDim, border: `1px solid ${TH.border}` }}>
                  PAST · backtest
                </span>
              )}
              <span style={{ color: TH.textDim }}>· composite {w.final_score}</span>
            </div>
            <div className="mt-1.5 text-xl font-light" style={{ color: TH.text }}>
              {w.from} <span style={{ color: TH.textDim }}>→</span> {w.to}
            </div>
            <div className="font-mono text-xs mt-1" style={{ color: TH.textMute }}>
              {w.md} → {w.ad} → {w.pd}
            </div>
          </div>
          <div className="flex gap-1.5">
            {greens.map((g) => (
              <div key={g.label} className="px-2 py-1 rounded-md font-mono text-[9px] font-bold flex items-center gap-1"
                style={{
                  backgroundColor: g.ok ? `${TH.green}22` : `${TH.rose}1A`,
                  color: g.ok ? TH.green : TH.rose,
                  border: `1px solid ${g.ok ? `${TH.green}55` : `${TH.rose}44`}`,
                }}
                title={g.note}
              >
                {g.ok ? <CheckCircle size={11} weight="fill" /> : <Warning size={11} weight="fill" />} {g.label}
              </div>
            ))}
          </div>
        </div>
        {expanded && (
          <div className="mt-4 pt-4 space-y-2" style={{ borderTop: `1px dashed ${TH.border}` }}>
            <div className="text-sm font-light leading-relaxed"
              style={{ color: TH.text }}
              dangerouslySetInnerHTML={{ __html: formatBold(w.narrative) }} />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-3">
              {greens.map((g) => (
                <div key={g.label} className="rounded-md px-2.5 py-2"
                  style={{ backgroundColor: TH.surfaceHi, border: `1px solid ${g.ok ? `${TH.green}33` : `${TH.rose}33`}` }}>
                  <div className="font-mono text-[9px] uppercase tracking-[0.2em] flex items-center gap-1"
                    style={{ color: g.ok ? TH.green : TH.rose }}>
                    {g.ok ? <CheckCircle size={9} weight="fill" /> : <Warning size={9} weight="fill" />} {g.label}
                  </div>
                  <div className="mt-1 text-[11px] leading-snug" style={{ color: TH.textMute }}>{g.note}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </button>
  );
};

const formatBold = (s) =>
  String(s || "").replace(/\*\*(.+?)\*\*/g, `<b style="color:${TH.gold}">$1</b>`);

// =============================================================================
// TOP PICKS — "If you want to try your luck in the next 3 years"
// =============================================================================
const TopPicksSection = ({ picks }) => (
  <section data-testid="lottery-top-picks">
    <div className="mb-3">
      <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em]" style={{ color: TH.gold }}>
        <Confetti size={14} weight="duotone" />
        // Try your luck — next 3 years
      </div>
      <div className="mt-1 text-sm sm:text-base font-light" style={{ color: TH.textMute }}>
        Top {picks.length} precision windows triangulated from KP 5-8-11 activation, sub-lord chain,
        Vimshottari MD-AD-PD, Kalachakra rashi-flow, Varshaphal year-lord, transit Jupiter on natal
        5H/11H, plus daily Moon-nakshatra + tithi + vaara scan for the sharpest ticket-buy days.
      </div>
    </div>
    <div className="grid sm:grid-cols-2 gap-4">
      {picks.map((p) => (
        <TopPickCard key={p.rank} p={p} />
      ))}
    </div>
  </section>
);

const TopPickCard = ({ p }) => {
  const tierStyle = TIER_STYLE[p.tier] || TIER_STYLE.GOOD;
  const conf = Math.max(0, Math.min(100, Number(p.confidence_pct) || 0));
  const confBar = `conic-gradient(${TH.gold} ${conf * 3.6}deg, ${TH.border} 0)`;
  return (
    <div className="rounded-2xl p-5 transition-all hover:shadow-2xl"
      style={{ background: `linear-gradient(155deg, ${TH.surface}, ${TH.surfaceHi})`,
               border: `1px solid ${tierStyle.border}`,
               boxShadow: `0 0 30px ${tierStyle.color}22` }}
      data-testid={`lottery-pick-${p.rank}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: TH.textDim }}>
            <Star size={12} weight="fill" style={{ color: TH.gold }} />
            Pick #{p.rank}
            <span className="px-2 py-0.5 rounded-full font-bold"
              style={{ backgroundColor: tierStyle.bg, color: tierStyle.color, border: `1px solid ${tierStyle.border}` }}>
              {tierStyle.label}
            </span>
          </div>
          <div className="mt-2 text-2xl font-light" style={{ color: TH.text }}>
            {p.month_range}
          </div>
          <div className="font-mono text-xs mt-1" style={{ color: TH.textMute }}>
            {p.md_ad_pd}
          </div>
        </div>
        {/* Confidence dial */}
        <div className="relative flex-shrink-0">
          <div className="rounded-full" style={{
            width: 64, height: 64, background: confBar,
          }} />
          <div className="absolute inset-1.5 rounded-full flex items-center justify-center"
            style={{ backgroundColor: TH.surface }}>
            <div className="text-center">
              <div className="text-base font-bold leading-none" style={{ color: TH.gold }}>{conf}%</div>
              <div className="text-[8px] font-mono uppercase tracking-wider" style={{ color: TH.textDim }}>conf</div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-3 text-sm font-light leading-relaxed" style={{ color: TH.text }}>
        {p.headline}
      </div>

      {/* Sharpest month */}
      <div className="mt-4 px-3 py-2 rounded-md font-mono text-xs"
        style={{ backgroundColor: `${TH.gold}11`, border: `1px solid ${TH.gold}44`, color: TH.gold }}>
        ⚡ Sharpest: <b>{p.sharpest_month}</b>
      </div>

      {/* Best buy days */}
      {p.best_buy_days && p.best_buy_days.length > 0 && (
        <div className="mt-3">
          <div className="font-mono text-[9px] uppercase tracking-[0.2em] mb-1.5" style={{ color: TH.textDim }}>
            Best ticket-buy days
          </div>
          <ul className="space-y-1.5">
            {p.best_buy_days.map((d) => (
              <li key={d.date} className="rounded-md px-2.5 py-1.5 font-mono text-[11px] flex flex-wrap items-center gap-2"
                style={{ backgroundColor: TH.surfaceHi, border: `1px solid ${TH.border}`, color: TH.text }}>
                <span className="font-bold" style={{ color: TH.gold }}>{d.date}</span>
                <span style={{ color: TH.textDim }}>·</span>
                <span>{d.weekday}</span>
                <span style={{ color: TH.textDim }}>·</span>
                <span>Moon in <b style={{ color: TH.green }}>{d.moon_nakshatra}</b></span>
                <span style={{ color: TH.textDim }}>·</span>
                <span>Tithi {d.tithi}</span>
                <span className="ml-auto px-1.5 py-0.5 rounded text-[9px] font-bold"
                  style={{ backgroundColor: `${TH.gold}22`, color: TH.gold }}>{d.score}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Booster checklist */}
      <div className="mt-3 grid grid-cols-1 gap-1.5 font-mono text-[10px]" style={{ color: TH.textMute }}>
        <div>✓ KP houses lit: <b style={{ color: TH.green }}>{(p.kp_houses_hit || []).map((h) => "H" + h).join(" · ")}</b></div>
        <div>✓ Kalachakra: {p.kalachakra_note}</div>
        <div>✓ Varshaphal: {p.varshaphal_note}</div>
        <div>{p.transit_jupiter?.hit ? "✓" : "○"} {p.transit_jupiter?.note}</div>
      </div>
    </div>
  );
};
